/** @jsxImportSource @emotion/react */
import { useSetState } from "ahooks";
import { isEmpty, partition, pick } from "lodash";
import { useEffect, useState } from "react";
import "./App.scss";
import FeedbackForm, { FormErrors } from "./FeedbackForm";
import { Banner, Footer, Header, Message } from "./components/Design";
import feedbackService from "./core/feedback.service";
import useEvent from "./hooks/useEvent";
import useFeedbackStyles from "./hooks/useFeedbackStyles";
import useParentMessages from "./hooks/useParentMessages";
import { StringContext } from "./hooks/useTranslation";
import { Feedback, Steps } from "./types/feedback.types";
import { setIn } from "./utils/object.utils";
import { computeFieldErrors } from "./utils/validate.utils";
import { filterVisibleByFields } from "./utils/visibleBy.utils";

type AppState = {
  data: Record<string, any>;
  errors: FormErrors;
  hasChanged?: boolean;
};

const { defaultValues } = window.__DATA__ || {};

function isInIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

const embed = isInIframe();

function noop(evt: any) {
  if (evt) {
    evt.stopPropagation();
    evt.preventDefault();
  }
}

function scrollToFirstError(errors: FormErrors) {
  for (const key in errors) {
    const element = document.getElementById(`feedback_${key}`) || document.getElementById(key);
    if (element && element.scrollIntoView) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
  }
}

function App() {
  const [user, setUser] = useState<any>(window.__DATA__.user || {});
  const [form, setForm] = useState<Feedback>(window.__DATA__ || {});

  useParentMessages({ setForm, setUser });

  const { title, theme, showTime, banner, footer, design, feedback, strings, isPreview, defaultStep, metadata } = form;
  const css = useFeedbackStyles(design);

  const [step, setStep] = useState<Steps>(defaultStep || Steps.GRADE);
  const [state, setState] = useSetState<AppState>(() => ({
    data: pick(defaultValues, form?.feedback?.form?.map((field) => field.name || field._id!) ?? []),
    errors: {},
    hasChanged: false,
  }));

  function updateStep(newStep: Steps) {
    setStep(newStep);

    if (window.parent) {
      window.parent.postMessage({ type: "feedback:step", payload: { step: newStep } }, "*");
    }
  }

  const { data, errors, hasChanged } = state;

  const fields = filterVisibleByFields(feedback.form || [], user, data);

  // TODO: use formik ?
  const handleChange = (key: string | string[], value: string) => {
    setState(({ data: prevData, errors }) => {
      const newData = setIn(prevData, key, value);

      // Empty errors mean we aren't checking them
      const newErrors = isEmpty(errors) ? {} : computeFieldErrors(fields, newData);
      return {
        data: newData,
        errors: newErrors,
        hasChanged: true,
      };
    });
  };

  const [topFields, bottomFields] = partition(fields, (f) => !f.afterButton);

  async function handlePost(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();

    // Check errors
    const errors = computeFieldErrors(fields, data);
    if (!isEmpty(errors)) {
      setState({ errors });
      scrollToFirstError(errors);
      return;
    }

    const result = {
      ...metadata,
      // Legacy data, remove ?
      // grade: rating,
      // comment: $("#comment").val(),
      ...data,
    };

    if (isPreview) {
      window.alert("Preview mode doesn't submit");
      updateStep(Steps.THANKS);
      return;
    }

    try {
      const res = await feedbackService.postForm(result);
      if (res) {
        updateStep(Steps.THANKS);
        if (window.__DATA__.goBackUrl) {
          window.location.href = window.__DATA__.goBackUrl;
        }
        return;
      }
    } catch (e) {
      console.error(e);
    }
  }

  const onBeforeUnload = useEvent((e: any) => {
    if (hasChanged && step === Steps.GRADE) {
      e.preventDefault();
      //necessary for Chrome
      e.returnValue = "";
    }
  });

  useEffect(() => {
    window.addEventListener("beforeunload", onBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (form.options?.expirationDate && new Date(form.options?.expirationDate).getTime() < Date.now()) {
    return (
      <div className="feedback-app">
        <div className="wrapper">
          <div className="wrapper-contents">
            <h3>{form.strings?.formClosed || "Le formulaire n'est plus accessible"}</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <StringContext.Provider value={strings}>
      <div css={css} className={`feedback-app theme-${theme || "default"} ${embed ? "feedback-app--embed" : ""}`}>
        <main className="wrapper">
          <Banner banner={banner || design?.banner} />

          <div className="wrapper-contents">
            <Header title={title} showTime={showTime && !!metadata?.datetime} time={metadata?.datetime} />

            {step === Steps.CANT_GRADE && (
              <section id="cant-grade" className="container">
                <p>{strings.cantGradeYet}</p>
              </section>
            )}
            {step === Steps.ALREADY_VOTED && (
              <section id="thanks" className="container" style={{ textAlign: "center", padding: 8 }}>
                <p>{strings.alreadyVoted}</p>
              </section>
            )}
            {step === Steps.THANKS && (
              <section id="thanks" className="container" style={{ textAlign: "center", padding: 8 }}>
                <p>{strings.thanks}</p>
              </section>
            )}
            {step === Steps.GRADE && (
              <section id="grading" className="container">
                <Message feedback={feedback} strings={strings} />

                <form onSubmit={noop}>
                  {/* <LegacyFeedback feedback={feedback} strings={strings} /> */}

                  <FeedbackForm fields={topFields} data={data} errors={errors} onChange={handleChange} />

                  <div style={{ textAlign: "center" }}>
                    <button id="validate" className="button" onClick={handlePost}>
                      {strings?.submit || "Envoyer"}
                    </button>
                  </div>

                  <FeedbackForm fields={bottomFields} data={data} errors={errors} onChange={handleChange} />
                </form>
              </section>
            )}
          </div>
          <Footer footer={footer || design?.footer} />
        </main>
      </div>
    </StringContext.Provider>
  );
}

export default App;
